import { createContext, createElement, FC, PropsWithChildren, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { drawerDefinitions, DrawerKeys } from './drawerDefinitions';
import { useSearchParams } from 'react-router-dom';
import * as qs from 'qs';
import { Drawer } from '@mui/material';

type Payload<T extends DrawerKeys> = z.infer<(typeof drawerDefinitions)[T]['payload']>;

type DrawerContextProps = Readonly<{
	drawerComponent: ReactNode;
	setDrawer: <T extends DrawerKeys>(type: T, payload: Payload<T>, callback?: () => void) => void;
	setDrawerWithoutPrevParams: <T extends DrawerKeys>(type: T, payload: Payload<T>, callback?: () => void) => void;
	closeDrawer: () => void;
}>;

const DrawerContext = createContext<DrawerContextProps>({
	drawerComponent: null,
	setDrawer: () => {},
	setDrawerWithoutPrevParams: () => {},
	closeDrawer: () => {},
});

const [firstKey, ...restOfKeys] = Object.keys(drawerDefinitions) as DrawerKeys[];

const initialSchema = z.object({
	// This ensures that there will always be at least one key => https://zod.dev/?id=zod-enums
	type: z.enum([firstKey!, ...restOfKeys]).optional(),
});

const DrawerContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const [selectedDrawer, setSelectedDrawer] = useState<ReactNode>(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const callbackRef = useRef<() => void | undefined>();

	useEffect(() => {
		const queryParams = qs.parse(searchParams.toString());
		if (!queryParams.drawer && selectedDrawer != null) {
			closeDrawer();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	useEffect(() => {
		const queryParams = qs.parse(searchParams.toString());
		// Check if drawer param exists within query string, so we do not check it when it is not necessary
		if (!queryParams.drawer) {
			return;
		}

		const drawerParams = queryParams.drawer as any; // any is okay in this case as this const is validated twice below

		if (initialSchema.safeParse(drawerParams).success) {
			const { component, payload } = drawerDefinitions[drawerParams.type as DrawerKeys];
			// Extend initial schema (this operation is non destructive so initial schema remains the same) and safe parse
			// with modal schema
			const parsedResult = z.intersection(initialSchema, payload).safeParse(drawerParams);
			if (parsedResult.success) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { type, ...props } = parsedResult.data;
				//@ts-expect-error
				setSelectedDrawer(createElement(component, props));
			} else {
				console.info('Parsing of query parameters failed', parsedResult.error);
			}
		}
	}, [searchParams]);

	// Get current query params and append drawer params
	const setDrawer: DrawerContextProps['setDrawer'] = (type, payload, callback) => {
		setSearchParams((prev) => {
			const queryParams = qs.parse(prev.toString());
			return qs.stringify({ ...queryParams, drawer: { type, ...payload } });
		});
		callbackRef.current = callback;
	};

	const setDrawerWithoutPrevParams: DrawerContextProps['setDrawer'] = (type, payload, callback) => {
		setSearchParams(() => {
			return qs.stringify({ drawer: { type, ...payload } });
		});
		callbackRef.current = callback;
	};

	// Remove drawer query params, but persist those non related to drawer
	const closeDrawer = () => {
		setSelectedDrawer(null);
		setSearchParams((prev) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { drawer, tab, ...rest } = qs.parse(prev.toString());
			return qs.stringify(rest);
		});
		callbackRef.current?.();
	};

	const drawerComponent = (
		<Drawer
			sx={{
				top: 0,
				zIndex: (theme) => theme.zIndex.drawer,
				[`& .MuiDrawer-paper`]: {
					boxSizing: 'border-box',
					position: 'absolute',
				},
				'& .MuiPaper-root': {
					boxShadow: (theme) => theme.customShadows.drawer,
				},
			}}
			open={selectedDrawer !== null}
			onClose={() => closeDrawer()}
			anchor={'right'}
			ModalProps={{ slotProps: { backdrop: { invisible: true } }, disablePortal: true }}
			variant={'temporary'}
		>
			{selectedDrawer}
		</Drawer>
	);

	return (
		<DrawerContext.Provider
			value={{
				drawerComponent,
				setDrawer,
				closeDrawer,
				setDrawerWithoutPrevParams,
			}}
		>
			{children}
		</DrawerContext.Provider>
	);
};

const useDrawerContext = (): DrawerContextProps => useContext(DrawerContext);

const DrawerComponent: FC = () => {
	const { drawerComponent } = useDrawerContext();
	return drawerComponent;
};

export { DrawerContextProvider, useDrawerContext, DrawerComponent };
